class PredictiveSearch extends HTMLFormElement {
  constructor() {
    super();
    this.cachedMap = new Map();
    this.focusElement = this.input;
    this.resetButton.addEventListener('click', this.clear.bind(this));
    this.input.addEventListener('input', FoxTheme.utils.debounce(this.onChange.bind(this), 300));
    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.searchContent = this.querySelector('.search__content');
    this.searchRecommendationEmpty = this.dataset.searchRecommendationEmpty === 'true';

    const isTemplateSearch = this.closest('.template-search');
    if (isTemplateSearch) {
      document.addEventListener('click', this.handleClickOutside.bind(this));
    }
  }

  get input() {
    return this.querySelector('input[type="search"]');
  }
  get resetButton() {
    return this.querySelector('button[type="reset"]');
  }

  onFocus(event) {
    if (this.closest('.template-search')) {
      document.body.classList.add('predictive-search-open');
      if (this.getQuery().length === 0) {
        return;
      }
      const url = this.setupURL().toString();
      this.renderSection(url, event);
    }
  }
  getQuery() {
    return this.input.value.trim();
  }
  clear(event = null) {
    event && event.preventDefault();
    if (this.searchRecommendationEmpty && this.searchContent) {
      this.searchContent.classList.add('hidden');
    }
    this.input.value = '';
    this.input.focus();
    this.removeAttribute('results');
  }

  setupURL() {
    const url = new URL(`${window.shopUrl}${FoxTheme.routes.predictive_search_url}`);
    return (
      url.searchParams.set('q', this.getQuery()),
      url.searchParams.set('resources[limit]', this.dataset.resultsLimit || 3),
      url.searchParams.set('resources[limit_scope]', 'each'),
      url.searchParams.set('resources[options][fields]', 'title,variants.sku'),
      url.searchParams.set('section_id', FoxTheme.utils.getSectionId(this)),
      url
    );
  }

  onChange() {
    if (this.getQuery().length === 0) {
      this.clear();
      return;
    }
    const url = this.setupURL().toString();
    this.renderSection(url);
  }

  renderSection(url) {
    this.cachedMap.has(url) ? this.renderSectionFromCache(url) : this.renderSectionFromFetch(url);
  }

  renderSectionFromCache(url) {
    const responseText = this.cachedMap.get(url);
    this.renderSearchResults(responseText), this.setAttribute('results', '');
  }

  renderSectionFromFetch(url) {
    // Indicate that loading has started
    this.setAttribute('loading', 'true');
    this.resetButton.classList.add('btn--loading');

    fetch(url)
      .then((response) => {
        if (!response.ok) {
          throw new Error('Network response was not ok');
        }
        return response.text();
      })
      .then((responseText) => {
        // Process and cache the fetched data
        this.renderSearchResults(responseText);
        this.cachedMap.set(url, responseText);
      })
      .catch((error) => {
        console.error('Error fetching data: ', error);
        this.setAttribute('error', 'Failed to load data');
      })
      .finally(() => {
        // Remove the loading attribute in both success and error cases
        this.removeAttribute('loading');
        this.resetButton.classList.remove('btn--loading');
        this.setAttribute('results', 'true');
      });
  }
  renderSearchResults(responseText) {
    const id = 'PredictiveSearchResults-' + FoxTheme.utils.getSectionId(this);
    const targetElement = document.getElementById(id);

    if (targetElement) {
      const parser = new DOMParser();
      const parsedDoc = parser.parseFromString(responseText, 'text/html');
      const contentElement = parsedDoc.getElementById(id);

      if (contentElement) {
        this.searchContent?.classList.remove('hidden');
        targetElement.innerHTML = contentElement.innerHTML;
        // Notify drawer (or any listener) that predictive results just rendered
        this.dispatchEvent(new CustomEvent('predictive-search:rendered', { bubbles: true }));
      } else {
        console.error(`Element with id '${id}' not found in the parsed response.`);
      }
    } else {
      console.error(`Element with id '${id}' not found in the document.`);
    }
  }

  handleClickOutside(event) {
    if (!this.contains(event.target)) {
      setTimeout(() => {
        document.body.classList.remove('predictive-search-open');
      });
    }
  }
}
customElements.define('predictive-search', PredictiveSearch, { extends: 'form' });

class SearchDrawer extends DrawerComponent {
  constructor() {
    super();
    this.initInfiniteScroll();
    this.initCloseOnOtherDrawerOpen();
  }
  get requiresBodyAppended() {
    return false;
  }
  get input() {
    return this.querySelector('input[type="search"]');
  }
  get focusElement() {
    return this.querySelector('input[type="search"]');
  }

  /* ------------------------------------------------------------------ *
   *  AUTO-CLOSE WHEN ANOTHER DRAWER / MODAL OPENS
   *  Watches the document for any element gaining `[open]` (cart drawer,
   *  menu drawer, modal, etc.). If something else opens while we're open,
   *  close ourselves so only one drawer is visible at a time.
   * ------------------------------------------------------------------ */
  initCloseOnOtherDrawerOpen() {
    // Observer #1: any element OUTSIDE the search drawer gaining [open]
    // (cart drawer, menu drawer, modal dialogs, megamenu <details-mega>, etc.)
    const openAttrObserver = new MutationObserver((mutations) => {
      if (!this.hasAttribute('open')) return;

      for (const m of mutations) {
        if (m.type !== 'attributes' || m.attributeName !== 'open') continue;
        const t = m.target;
        if (t === this) continue;
        if (this.contains(t)) continue;
        if (!t.hasAttribute('open')) continue;
        if (typeof this.hide === 'function') this.hide();
        break;
      }
    });
    openAttrObserver.observe(document.body, {
      attributes: true,
      attributeFilter: ['open'],
      subtree: true,
    });

    // Observer #2: body gaining `has-dropdown-menu` class — the theme adds
    // this whenever a dropdown / megamenu opens (header.js DetailsDropdown
    // line ~226). Belt-and-suspenders coverage in case a dropdown sets
    // [open] in a way Observer #1 misses.
    const bodyClassObserver = new MutationObserver(() => {
      if (!this.hasAttribute('open')) return;
      if (document.body.classList.contains('has-dropdown-menu')) {
        if (typeof this.hide === 'function') this.hide();
      }
    });
    bodyClassObserver.observe(document.body, {
      attributes: true,
      attributeFilter: ['class'],
    });
  }

  /* ------------------------------------------------------------------ *
   *  INFINITE SCROLL
   *  - listens for `predictive-search:rendered` to seed dedupe + reset paging
   *  - on scroll near bottom of drawer body, fetches /search?view=ajax&page=N
   *  - appends new product cards (skips dupes by data-product-id)
   * ------------------------------------------------------------------ */
  initInfiniteScroll() {
    this.scrollState = {
      currentQuery: '',
      currentPage: 1,
      hasNext: true,
      loading: false,
      seenProductIds: new Set(),
    };

    this.body = this.querySelector('.search-drawer__body');
    if (!this.body) return;

    this.addEventListener('predictive-search:rendered', this.onPredictiveRendered.bind(this));
    this.body.addEventListener('scroll', this.onDrawerScroll.bind(this), { passive: true });
  }

  getProductsGrid() {
    return this.querySelector('.search__products-grid');
  }

  onPredictiveRendered() {
    const inputEl = this.input;
    if (!inputEl) return;
    const query = inputEl.value.trim();

    // Reset state for new query
    this.scrollState.currentQuery = query;
    this.scrollState.currentPage = 1;
    this.scrollState.hasNext = true;
    this.scrollState.loading = false;
    this.scrollState.seenProductIds.clear();

    // Seed seen product IDs from predictive results that are already in the DOM
    const grid = this.getProductsGrid();
    if (grid) {
      grid.querySelectorAll('[data-product-id]').forEach((el) => {
        this.scrollState.seenProductIds.add(el.dataset.productId);
      });
    }
  }

  onDrawerScroll() {
    const state = this.scrollState;
    if (state.loading || !state.hasNext) return;
    if (!state.currentQuery || state.currentQuery.length === 0) return;

    const body = this.body;
    const distFromBottom = body.scrollHeight - (body.scrollTop + body.clientHeight);
    if (distFromBottom > 600) return;

    this.loadNextPage();
  }

  async loadNextPage() {
    const state = this.scrollState;
    state.loading = true;
    state.currentPage += 1;

    const url = `${window.shopUrl || ''}/search?q=${encodeURIComponent(
      state.currentQuery
    )}&type=product&view=ajax&page=${state.currentPage}`;

    try {
      const res = await fetch(url);
      if (!res.ok) throw new Error('Network error: ' + res.status);
      const html = await res.text();

      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      const payload = doc.querySelector('.search-drawer-ajax-payload');
      if (!payload) {
        state.hasNext = false;
        return;
      }

      state.hasNext = payload.dataset.hasNext === 'true';

      const grid = this.getProductsGrid();
      if (!grid) return;

      const newCards = payload.querySelectorAll('[data-product-id]');
      let appended = 0;
      newCards.forEach((card) => {
        const pid = card.dataset.productId;
        if (state.seenProductIds.has(pid)) return;
        state.seenProductIds.add(pid);
        grid.appendChild(card);
        appended += 1;
      });

      // Safety: if a fetched page yielded zero new products, stop paging
      if (appended === 0) state.hasNext = false;
    } catch (err) {
      console.error('Search drawer infinite scroll error:', err);
      state.hasNext = false;
    } finally {
      state.loading = false;
    }
  }
}
customElements.define('search-drawer', SearchDrawer);